import { WidgetImpactOptions } from 'app/modules/widget/components/widget-impact/widget-impact.component';
import { Comment, Dashboard, Widget } from 'app/providers/bridge-api/dashboard.service';
import { Report } from 'app/providers/storage-api/report.service';
import { FilterUtils } from 'app/utils/filter.utils';
import { WidgetDataSource } from '../../../../../models';
import {
  AbstractChartReport,
  CommentDataChange,
} from '../chart-report-abstract/chart-report-abstract';

export const COMMENT_POINT_ID_SEPARATOR = '|x|';

export interface ImpactDataItem {
  label: string;
  labelDisplay: string;
  documentCount: number;
  leftScore?: number;
  rightScore: number;
  change?: number;
  selectionState?: {
    left?: {
      selected: boolean;
      index: number;
    };
    right?: {
      selected: boolean;
      index: number;
    };
  };
}

export interface ImpactData {
  data: ImpactDataItem[];
}

export class ImpactReport extends AbstractChartReport {
  reportName = 'featureImpact';
  reportHandler = (_dashboard, _widget, _response, _options) => {
    return {};
  };

  getFields(): string[] {
    return [];
  }

  canSort(): boolean {
    return false;
  }

  getGraphTypes() {
    return [];
  }

  public canSetChartMaximumHorizontal(): boolean {
    return true;
  }

  isAnalyticItemSupported(key) {
    return !['documents'].includes(key);
  }

  public getAnalyticItemNotSupportedTooltip(analyticItem: string): string {
    switch (analyticItem) {
      case 'documents':
        return undefined;
    }
  }

  public async executeReportForImpact(
    additionalParams: any,
    widgetDataSource: WidgetDataSource,
    query: string,
    format: 'json' | 'csv' = 'json'
  ): Promise<ImpactData> {
    const params = await this.getParams();
    for (const key of Object.keys(additionalParams)) {
      params[key] = additionalParams[key];
    }

    // DASH-9694: Add comparison section filters as featureSectionFilters
    if (params.comparisonSectionFilters) {
      params.featureSectionFilter ??= [];
      params.featureSectionFilter.push(
        ...FilterUtils.convertSelectedSectionToFeatureSectionFilter(params.comparisonSectionFilters)
      );
      delete params.comparisonSectionFilters;
    }

    const response = (await this.reportService.getReport(
      widgetDataSource.collectionName,
      widgetDataSource.groupId,
      this.reportName,
      params,
      format,
      query,
      null,
      null,
      this.options.projectId
    )) as Report<{ [p: string]: any }>;

    this.lastDocumentCount = response.totalHits;

    return this.processResponse(response);
  }

  public async processResponse(response): Promise<ImpactData> {
    const data = [];
    const report = response.items;
    if (!Object.keys(report).length) {
      return { data };
    }

    const scoreExtractor = this.scoreExtractorFn(
      this.options.scoringStrategy,
      this.options.boxDirection,
      this.options.bucketName
    );

    for (const item of report.impact.scores) {
      let displayValue = item.label;
      if (this.options.analyticItem === 'taxonomy') {
        if (response.extra) {
          displayValue = response.extra[item.label]
            ? response.extra[item.label]['name']
            : item.label;
        }
      }
      data.push({
        label: item.label,
        labelDisplay: displayValue,
        rightScore: scoreExtractor(item),
        documentCount: item.docCount,
      });
    }

    return { data };
  }

  private scoreExtractorFn(scoringStrategy, boxDirection?, bucketName?: string) {
    switch (scoringStrategy) {
      case 'AVERAGE':
      case 'NPS':
        return (item) => item.score;
      case 'BOX':
        if (boxDirection === 'TOP') {
          return (item) => item.topScore;
        }
        return (item) => item.bottomScore;
      case 'BUCKET':
        return (item) => item[`${bucketName}Score`];
    }
  }

  protected getPointForComment(comment: Comment, series: any) {
    const [rowId, columnId] = comment.target?.pointId?.split(COMMENT_POINT_ID_SEPARATOR);
    if (rowId && columnId) {
      const value = series.find((s) => s.label === rowId)?.[`${columnId}Score`] ?? 0;

      return {
        x: value,
        y: value,
      };
    }
  }

  isCommentVisible(comment: Comment, widget: Widget, series) {
    if (!series) {
      return false;
    }

    const [rowId] = comment.target?.pointId?.split(COMMENT_POINT_ID_SEPARATOR);
    return series.find((s) => s.label === rowId);
  }

  protected getReportSpecificCommentDataChange(
    comment: Comment,
    widget: Widget<WidgetImpactOptions>,
    dashboard: Dashboard,
    currentData: any,
    dataProp: string
  ): CommentDataChange {
    const changeReasons = [];
    if (comment.payload.ratingField !== widget.payload.ratingField) {
      changeReasons.push('RATING_FIELD_CHANGED');
    }

    if (comment.payload.ratingScale !== widget.payload.ratingScale) {
      changeReasons.push('RATING_SCALE_CHANGED');
    }

    if (comment.payload.scoringStrategy !== widget.payload.scoringStrategy) {
      changeReasons.push('SCORING_STRATEGY_CHANGED');
    }

    /* eslint-disable-next-line eqeqeq */
    if (comment.payload.boxSize != widget.payload.boxSize) {
      changeReasons.push('BOX_SIZE_CHANGED');
    }

    /* eslint-disable-next-line eqeqeq */
    if (comment.payload.boxDirection != widget.payload.boxDirection) {
      changeReasons.push('BOX_DIRECTION_CHANGED');
    }

    if (changeReasons.length) {
      return {
        [dataProp]: currentData?.[dataProp],
        reasons: changeReasons,
      };
    }
  }
}
